import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getLocalizedPath, type RouteKey } from '../../i18n/routes'
import { useI18n } from '../../i18n/I18nProvider'
import { useRouteMetadata } from '../inventory/useRouteMetadata'
import { Link } from '../../ui/Link'
import { CommercialLink } from '../../ui/CommercialLink'
import { VerificationMark } from '../../ui/VerificationMark'
import { PageHero } from '../page-hero/PageHero'
import { translateEditorial, type EditorialMessageKey } from './messages'
import './editorial.css'

type Translator = (key: EditorialMessageKey) => string

function EditorialContact({ t }: { t: Translator }) {
  const { language } = useI18n()
  return (
    <section className="editorial-contact commercial-cta" aria-labelledby="editorial-contact-title">
      <div className="page-frame editorial-contact__grid">
        <h2 id="editorial-contact-title">{t('servicesContactTitle')}</h2>
        <div>
          <p>{t('servicesContactBody')}</p>
          <CommercialLink to={getLocalizedPath('contacts', language)}>{t('servicesContactAction')}</CommercialLink>
        </div>
      </div>
    </section>
  )
}

function useEditorialTranslation() {
  const { language } = useI18n()
  return useCallback((key: EditorialMessageKey) => translateEditorial(language, key), [language])
}

const services: Array<{
  label: EditorialMessageKey
  title: EditorialMessageKey
  body: EditorialMessageKey
  image: string
  imageAlt: EditorialMessageKey
}> = [
  { label: 'service1Label', title: 'service1Title', body: 'service1Body', image: '/media/services/selection.webp', imageAlt: 'service1ImageAlt' },
  { label: 'service2Label', title: 'service2Title', body: 'service2Body', image: '/media/services/finance.webp', imageAlt: 'service2ImageAlt' },
  { label: 'service3Label', title: 'service3Title', body: 'service3Body', image: '/media/services/paperwork.webp', imageAlt: 'service3ImageAlt' },
  { label: 'service4Label', title: 'service4Title', body: 'service4Body', image: '/media/services/assistance.webp', imageAlt: 'service4ImageAlt' },
  { label: 'service5Label', title: 'service5Title', body: 'service5Body', image: '/media/services/rental.webp', imageAlt: 'service5ImageAlt' },
]

export function ServicesPage() {
  const { language } = useI18n()
  const { pathname } = useLocation()
  const t = useEditorialTranslation()
  useRouteMetadata({
    title: t('servicesMetadataTitle'),
    description: t('servicesMetadataDescription'),
    pathname,
    language,
  })
  return (
    <div className="editorial-page editorial-page--services">
      <PageHero variant="services" eyebrow={t('servicesEyebrow')} title={t('servicesTitle')}>
        <p>{t('servicesBody')}</p>
      </PageHero>

      <section className="service-sequence" aria-label={t('servicesTitle')}>
        <ol role="list">
          {services.map(({ label, title, body, image, imageAlt }, index) => (
            <li className="service-section" key={title}>
              <article className="page-frame service-section__grid">
                <div className="service-section__index" aria-hidden="true">
                  <span className="service-section__number">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <figure className="service-section__media">
                  <img src={image} alt={t(imageAlt)} width="1600" height="1200" loading="lazy" decoding="async" />
                </figure>
                <div className="service-section__heading">
                  <p className="service-section__label">{t(label)}</p>
                  <h2>{t(title)}</h2>
                </div>
                <div className="service-section__content">
                  <p>{t(body)}</p>
                  {index === services.length - 1 ? (
                    <Link variant="text" to={getLocalizedPath('shortRental', language)}>{t('serviceRentalAction')}</Link>
                  ) : null}
                </div>
              </article>
            </li>
          ))}
        </ol>
      </section>
      <EditorialContact t={t} />
    </div>
  )
}

export function ServiceRequestPage({ route }: { route: Extract<RouteKey, 'shortRental' | 'booking'> }) {
  const { language } = useI18n()
  const { pathname } = useLocation()
  const t = useEditorialTranslation()
  const isRental = route === 'shortRental'
  const title = isRental ? 'rentalTitle' : 'requestTitle'
  const body = isRental ? 'rentalBody' : 'requestBody'
  const action = isRental ? 'rentalContactAction' : 'requestContactAction'
  const contactLabel = isRental ? 'rentalContactLabel' : 'requestContactLabel'
  useRouteMetadata({
    title: t(isRental ? 'rentalMetadataTitle' : 'requestMetadataTitle'),
    description: t(isRental ? 'rentalMetadataDescription' : 'requestMetadataDescription'),
    pathname,
    language,
  })
  return (
    <div className="editorial-page editorial-page--request">
      <PageHero variant="request" eyebrow={t('requestEyebrow')} title={t(title)}>
        <p>{t(body)}</p>
      </PageHero>
      <section className="request-next commercial-cta" aria-label={t(contactLabel)}>
        <div className="page-frame request-next__inner">
          <VerificationMark label={t(contactLabel)} />
          <CommercialLink to={getLocalizedPath('contacts', language)}>{t(action)}</CommercialLink>
        </div>
      </section>
    </div>
  )
}
